import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Logo } from '@/components/logos/logo';
import shopifyMapping from '@contextual/shared/shopify_mapping.json' assert { type: 'json' };
import { GetServerEventLogsQuery, IntegrationType } from '@/gql/graphql';
import { useMemo } from 'react';
import { formatNumber } from '@/utils/dashboard-utils';
import { TableSkeleton } from '@/components/ui/skeletons';
const SHOPIFY_INTEGRATION_ID = '$shopify';

type IntegrationOverrides = Partial<
  Record<
    string,
    {
      name: string;
      type: typeof SHOPIFY_INTEGRATION_ID;
    }
  >
> & {
  [SHOPIFY_INTEGRATION_ID]: {
    name: string;
    type: typeof SHOPIFY_INTEGRATION_ID;
  };
};

const INTEGRATION_OVERRIDES: IntegrationOverrides = {
  [SHOPIFY_INTEGRATION_ID]: {
    name: 'Shopify',
    type: '$shopify' as const
  }
};

type EventData = Record<string, number>;

interface IntegrationData {
  integrationName: string;
  type: IntegrationType | typeof SHOPIFY_INTEGRATION_ID;
  events: EventData;
  integrationId: string;
}

// Define valid icon types
type IconType =
  | 'contextual'
  | 'meta'
  | 'shopify'
  | 'ga4'
  | 'klaviyo'
  | 'subdomain'
  | 'postscript'
  | 'tiktok'
  | 'snapchat'
  | 'googleads';
const processServerEventLogs = (metrics: GetServerEventLogsQuery['getServerEventLogs']) => {
  if (metrics.length === 0) return { eventNames: [], processedData: [] };

  // Extract unique event names and integration data
  const eventNamesSet = new Set<string>();
  const integrationsObj: Record<string, IntegrationData> = {};

  metrics.forEach((metric) => {
    const [eventName, integrationId, integrationName, integrationType] = metric.labels;
    eventNamesSet.add(eventName);

    if (!(integrationId in integrationsObj)) {
      const override = INTEGRATION_OVERRIDES[integrationId];
      integrationsObj[integrationId] = {
        integrationId,
        integrationName: override?.name ?? integrationName,
        type: override?.type ?? (integrationType as IntegrationType),
        events: {}
      };
    }

    integrationsObj[integrationId].events[eventName] = metric.aggregate;
  });

  // Convert to sorted array of event names
  const eventNames = Array.from(eventNamesSet).sort();

  // Convert integrations object to array
  const processedData = Object.values(integrationsObj).sort((a, b) => {
    // Always put Shopify first
    if (a.integrationId === SHOPIFY_INTEGRATION_ID) return -1;
    if (b.integrationId === SHOPIFY_INTEGRATION_ID) return 1;
    // Sort remaining alphabetically by integration name
    return a.integrationName.localeCompare(b.integrationName);
  });

  return { eventNames, processedData };
};

export function ServerEventsLog({
  isLoading,
  serverEventLogsData
}: {
  isLoading: boolean;
  serverEventLogsData: GetServerEventLogsQuery | undefined;
}) {
  const { eventNames, processedData } = useMemo(
    () => processServerEventLogs(serverEventLogsData?.getServerEventLogs ?? []),
    [serverEventLogsData?.getServerEventLogs]
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="p-4">
          <CardTitle className="text-lg font-semibold text-card-foreground">
            Server Events Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableSkeleton rowCount={3} columnCount={4} showIcon={true} />
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (
    !serverEventLogsData?.getServerEventLogs ||
    serverEventLogsData.getServerEventLogs.length === 0
  ) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-card-foreground">
            Server Events Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-card-foreground">No Data Available</div>
        </CardContent>
      </Card>
    );
  }

  // Function to map integration type to valid icon type
  const getIconType = (type: string): IconType => {
    const iconMap: Record<string, IconType> = {
      META: 'meta',
      KLAVIYO: 'klaviyo',
      SHOPIFY: 'shopify',
      GA4: 'ga4',
      POSTSCRIPT: 'postscript',
      $shopify: 'shopify',
      TIKTOK: 'tiktok',
      SNAPCHAT: 'snapchat',
      GOOGLEADS: 'googleads'
    };
    return iconMap[type];
  };

  const getCtxName = (eventName: string): string => {
    const mapping = shopifyMapping[eventName as keyof typeof shopifyMapping];
    return mapping.ctx_name || eventName;
  };

  return (
    <Card>
      <CardHeader className="p-4">
        <CardTitle className="text-lg font-semibold text-card-foreground">
          Server Events Log
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table className="table-auto">
            <TableHeader>
              <TableRow>
                <TableHead>Integration</TableHead>
                {eventNames.map((eventName) => (
                  <TableHead key={eventName} className="px-6 whitespace-nowrap">
                    {getCtxName(eventName)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {processedData.map((integration) => (
                <TableRow key={integration.integrationId}>
                  <TableCell className="font-medium">
                    <div className="flex items-center space-x-2">
                      <Logo icon={getIconType(integration.type)} />
                      <span>{integration.integrationName}</span>
                    </div>
                  </TableCell>
                  {eventNames.map((eventName) => (
                    <TableCell key={eventName} className="text-center px-6">
                      {integration.events[eventName]
                        ? formatNumber(integration.events[eventName])
                        : '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
